import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  Form,
  FormGroup,
  Input,
  Label,
  CardFooter,
  Badge,
  UncontrolledTooltip,
  Col,
  Row,
} from "reactstrap";
import CommonTable from "components/CommonComponents/ReactTable/Table";
import Select, { components } from "react-select";
import "react-quill/dist/quill.snow.css";
import {
  DEFAULT_RECORDS_PER_PAGE,
  ASSESSMENT_STATUS_OPTIONS,
} from "components/constant";
import { fixedWidthCustomStyle } from "constants/CommonConstants";
import {
  ISOToHumanReadableDate,
  ISOToHumanReadableWithTimeDate,
} from "components/CommonComponents/CommonFunctions";
import InfoPopover from "components/CommonComponents/InfoPopover";
import { useLocation, useNavigate } from "react-router-dom";
import { urlEncodedId } from "components/CommonComponents/encodeDecodeFunction";
import AddQuestion from "./AddQuestion";
import ExistingQuestionModal from "pages/QuestionBank/ExistingQuestionModal";
import DatePicker from "react-datepicker";

const assessmentList = [
  {
    id: 1,
    assessmentName: "Electrical Safety Basics Test",
    assessmentDuration: 60, // minutes
    status: "ACTIVE",
    assignedBy: "Admin",
    createdOn: "2025-02-01",
    availableFrom: "2025-02-01T09:00",
    availableTill: "2025-02-10T17:00",
    maxAttempts: 3,
    passingPercentage: 70,
  },
  {
    id: 2,
    assessmentName: "Transformer Testing Quiz",
    assessmentDuration: 45,
    status: "UPCOMING",
    assignedBy: "Trainer",
    createdOn: "2025-02-05",
    availableFrom: "2025-02-10T09:00",
    availableTill: "2025-02-20T17:00",
    maxAttempts: 2,
    passingPercentage: 80,
  },
  {
    id: 3,
    assessmentName: "Substation Maintenance Assessment",
    assessmentDuration: 90,
    status: "ACTIVE",
    assignedBy: "Admin",
    createdOn: "2025-02-10",
    availableFrom: "2025-02-11T09:00",
    availableTill: "2025-02-25T17:00",
    maxAttempts: 1,
    passingPercentage: 75,
  },
  {
    id: 4,
    assessmentName: "HT & LT Systems Final Test",
    assessmentDuration: 120,
    status: "CLOSED",
    assignedBy: "Trainer",
    createdOn: "2025-02-15",
    availableFrom: "2025-02-16T09:00",
    availableTill: "2025-02-28T17:00",
    maxAttempts: 3,
    passingPercentage: 60,
  },
  {
    id: 5,
    assessmentName: "Cable Jointing Practical Test",
    assessmentDuration: 75,
    status: "UPCOMING",
    assignedBy: "Admin",
    createdOn: "2025-02-18",
    availableFrom: "2025-02-20T09:00",
    availableTill: "2025-03-05T17:00",
    maxAttempts: 2,
    passingPercentage: 85,
  },
  {
    id: 6,
    assessmentName: "Transformer Testing Quiz",
    assessmentDuration: 50,
    status: "UPCOMING",
    assignedBy: "Trainer",
    createdOn: "2025-10-05",
    availableFrom: "2025-10-10T09:00",
    availableTill: "2025-10-20T17:00",
    maxAttempts: 3,
    passingPercentage: 75,
  },
];

const initialFields = {
  assessmentName: {
    value: "",
    error: "",
  },
  assessmentDuration: {
    value: "",
    error: "",
  },
  availableFrom: {
    value: "",
    error: "",
  },
  availableTill: {
    value: "",
    error: "",
  },
  maxAttempts: {
    value: "",
    error: "",
  },
  passingPercentage: {
    value: "",
    error: "",
  },
  status: {
    value: "INACTIVE",
    error: "",
  },
};

const initialQBQuestion = {
  question: {
    en: "",
    hi: "",
    error: "",
  },
  type: { value: null, error: "" },
  marks: { value: "", error: "" },
  options: {
    value: [
      { text: "", grade: null },
      { text: "", grade: null },
    ],
    error: "",
  },
};

function Assessment({ courseId }) {
  const navigate = useNavigate();
  const authData = JSON.parse(localStorage.getItem("p") || "{}");
  const role = authData?.role;
  const rolePath =
    role === "SUPER_ADMIN"
      ? "super-admin"
      : role === "ADMIN"
        ? "admin"
        : role === "CHANNEL_PARTNER"
          ? "channel-partner"
          : "instructor";
  const location = useLocation();
  const { isEdit } = location.state || "false";
  const [viewMode, setViewMode] = useState("LIST");
  const [formData, setFormData] = useState(initialFields);
  const [pageNum, setPageNum] = useState(1);
  const [searchState, setSearchState] = useState({
    name: "",
    status: null,
    filterParams: [],
  });
  const [isQBPanelOpen, setIsQBPanelOpen] = useState(false);
  const [qbQuestions, setQbQuestions] = useState([]);
  const [currentQB, setCurrentQB] = useState(initialQBQuestion);
  const [negativeGrading, setNegativeGrading] = useState(false);
  const [qbPanelMode, setQbPanelMode] = useState("ADD"); // ADD | VIEW
  const [selectedQuestionIndex, setSelectedQuestionIndex] = useState(null);
  const [showExistingModal, setShowExistingModal] = useState(false);

  useEffect(() => {
    if (isEdit === true) {
      openEditModal(assessmentList[0]);
    }
  }, [isEdit]);

  const openAddModal = () => {
    setViewMode("ADD");
    setFormData(initialFields);
  };

  const openEditModal = (assessment) => {
    setViewMode("EDIT");

    setFormData({
      assessmentName: {
        value: assessment.assessmentName,
        error: "",
      },
      assessmentDuration: {
        value: assessment.assessmentDuration,
        error: "",
      },
      availableFrom: {
        value: assessment.availableFrom,
        error: "",
      },
      availableTill: {
        value: assessment.availableTill,
        error: "",
      },
      maxAttempts: {
        value: assessment.maxAttempts,
        error: "",
      },
      passingPercentage: {
        value: assessment.passingPercentage,
        error: "",
      },
      status: {
        value: assessment.status,
        error: "",
      },
    });
  };

  const goBackToList = () => {
    setViewMode("LIST");
    setFormData(initialFields);
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => {
      let updatedState = {
        ...prev,
        [field]: {
          value,
          error: "",
        },
      };

      return updatedState;
    });
  };

  const openViewQuestion = (question, index) => {
    setCurrentQB({
      question: {
        en: question.question?.en || "",
        hi: question.question?.hi || "",
        error: "",
      },
      type: { value: question.type, error: "" },
      marks: { value: question.marks ?? "", error: "" },
      options: {
        value: question.options.map((o) => ({ ...o })),
        error: "",
      },
    });

    setNegativeGrading(question.negativeGrading);
    setSelectedQuestionIndex(index);
    setQbPanelMode("VIEW");
    setIsQBPanelOpen(true);
  };

  const validateForm = () => {
    let newSkill = { ...formData };

    Object.keys(newSkill).forEach((key) => {
      newSkill[key] = {
        ...newSkill[key],
        error: getValidationError(key, newSkill[key].value),
      };
    });

    setFormData(newSkill);
    return Object.values(newSkill).every((item) => item.error === "");
  };

  //on blur to get if any validation error exits
  const handleOnBlur = (name, value) => {
    const trimmedValue = typeof value === "string" ? value.trim() : value;
    const error = getValidationError(name, trimmedValue);
    setFormData((prev) => ({
      ...prev,
      [name]: {
        ...prev[name],
        value: trimmedValue,
        error,
      },
    }));
  };

  const handleSearch = () => { };

  const getValidationError = (name, value) => {
    switch (name) {
      case "assessmentName":
        if (!value) return "Assessment name is required";
        return "";

      case "assessmentDuration":
        if (!value) return "Assessment duration is required";
        if (value <= 0) return "Duration must be greater than 0";
        return "";

      case "availableFrom":
        if (!value) return "Available from date & time is required";
        return "";

      case "availableTill":
        if (!value) return "Available till date & time is required";
        if (
          formData.availableFrom.value &&
          new Date(value) <= new Date(formData.availableFrom.value)
        ) {
          return "Till date must be after From date";
        }
        return "";

      case "maxAttempts":
        if (!value) return "Maximum attempts is required";
        if (value <= 0) return "Must be greater than 0";
        return "";

      case "passingPercentage":
        if (!value && value !== 0) return "Passing percentage is required";
        if (value < 0 || value > 100)
          return "Percentage must be between 0 and 100";
        return "";

      default:
        return "";
    }
  };

  // Custom Option Renderer
  const Option = (props) => {
    return (
      <components.Option {...props}>
        <Badge
          color={
            props.data.value === "ACTIVE"
              ? "success"
              : props.data.value === "UPCOMING"
                ? "warning"
                : props.data.value === "CLOSED"
                  ? "secondary"
                  : "info"
          }
          pill
        >
          {props.data.label}
        </Badge>
      </components.Option>
    );
  };

  // Custom SingleValue Renderer (for the selected value)
  const SingleValue = (props) => {
    return (
      <components.SingleValue {...props}>
        <Badge
          color={
            props.data.value === "ACTIVE"
              ? "success"
              : props.data.value === "UPCOMING"
                ? "warning"
                : props.data.value === "CLOSED"
                  ? "secondary"
                  : "info"
          }
          pill
        >
          {props.data.label}
        </Badge>
      </components.SingleValue>
    );
  };

  const getFreshQBQuestion = () => ({
    question: { value: "", error: "" },
    type: { value: null, error: "" },
    marks: { value: "", error: "" },
    options: {
      value: [
        { text: "", grade: null },
        { text: "", grade: null },
      ],
      error: "",
    },
  });

  const closeModal = () => {
    setCurrentQB(getFreshQBQuestion());
    setIsQBPanelOpen(false);
  };

  const handleClear = () => {
    setSearchState({
      query: "",
      status: null,
      filterParams: [],
    });
    if (pageNum !== 1) setPageNum(1);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  const resetQBPanel = () => {
    setCurrentQB(getFreshQBQuestion());
    setIsQBPanelOpen(true);
  };

  const columns = [
    {
      Header: "Sr. No.",
      accessor: "id",
    },
    {
      Header: "Assessment Name",
      accessor: "assessmentName",
    },
    {
      Header: "Duration",
      accessor: "assessmentDuration",
      Cell: ({ value }) => (value ? `${value} min` : "-"),
    },
    {
      Header: "Max Attempts",
      accessor: "maxAttempts",
    },
    {
      Header: "Passing %",
      accessor: "passingPercentage",
      Cell: ({ value }) => (value !== undefined ? `${value}%` : "-"),
    },
    {
      Header: "Availability",
      accessor: "availability",
      Cell: ({ row }) => {
        const { availableFrom, availableTill } = row.original;
        if (!availableFrom && !availableTill) return "-";

        return (
          <div>
            <div className="d-flex align-items-center mb-1">
              <span className="mr-1">From:</span>
              <span>{ISOToHumanReadableWithTimeDate(availableFrom)}</span>
            </div>
            <div className="d-flex align-items-center">
              <span className="mr-1">To:</span>
              <span className="pl-3">
                {ISOToHumanReadableWithTimeDate(availableTill)}
              </span>
            </div>
          </div>
        );
      },
    },
    {
      Header: "Assigned By",
      accessor: "assignedBy",
    },
    {
      Header: "Created On",
      accessor: "createdOn",
      Cell: ({ value }) => ISOToHumanReadableDate(value),
    },
    {
      Header: "Status",
      accessor: "status",
      Cell: ({ row }) => {
        const { availableFrom, availableTill } = row.original;
        const status = deriveStatus(availableFrom, availableTill);

        const color =
          status === "ACTIVE"
            ? "success"
            : status === "UPCOMING"
              ? "warning"
              : "secondary";

        return (
          <Badge color={color} pill>
            {status}
          </Badge>
        );
      },
    },
    {
      Header: "Actions",
      accessor: "actions",
      Cell: ({ row }) => {
        const assessment = row.original;
        const encodedId = urlEncodedId(assessment.id);
        const encodedCourseId = urlEncodedId(courseId);

        return (
          <div className="d-flex align-items-center">
            {/* View */}
            <Button
              color="link"
              className="p-0"
              id={`view${encodedId}`}
              onClick={() =>
                navigate(
                  `/${rolePath}/course/${encodedCourseId}/details/${encodedId}?tab=assessment`,
                )
              }
            >
              <i className="fa-regular fa-eye" />
            </Button>
            <UncontrolledTooltip delay={0} target={`view${encodedId}`}>
              View
            </UncontrolledTooltip>

            {/* Edit */}
            <Button
              color="link"
              className="p-0 ml-2"
              id={`edit${encodedId}`}
              onClick={() => openEditModal(assessment)}
            >
              <i className="fas fa-edit m-0 ml-1" />
            </Button>
            <UncontrolledTooltip delay={0} target={`edit${encodedId}`}>
              Edit
            </UncontrolledTooltip>

            {/* Divider */}
            <span className="mx-1 text-muted">|</span>
            {/* Delete */}
            <Button
              color="link"
              className="btn-link d-flex align-items-center p-0 table-action-delete"
              id={`delete${encodedId}`}
            >
              <i className="fas fa-trash mx-1 table-action-delete" />
            </Button>
            <UncontrolledTooltip delay={0} target={`delete${encodedId}`}>
              Delete
            </UncontrolledTooltip>
          </div>
        );
      },
    },
  ];

  const questionColumns = [
    {
      Header: "Sr. No.",
      accessor: "srNo",
    },
    {
      Header: "Question",
      accessor: "question",
      Cell: ({ value }) => (
        <div
          dangerouslySetInnerHTML={{
            __html: value?.en || value?.hi || "",
          }}
          className="max-width-25"
        />
      ),
    },
    {
      Header: "Options",
      accessor: "options",
      Cell: ({ row }) => {
        const { type, options } = row.original;

        // Description questions have no options
        if (type === "DESC") {
          return <span className="text-muted">—</span>;
        }

        return (
          <div className="max-width-25">
            {options?.map((opt, idx) => (
              <div key={idx} className="d-flex align-items-baseline mb-1">
                <strong className="mr-1">{idx + 1}.</strong>
                <div dangerouslySetInnerHTML={{ __html: opt.text }} />
              </div>
            ))}
          </div>
        );
      },
    },
    {
      Header: "Type",
      accessor: "type",
      Cell: ({ value }) =>
        value === "MCQ"
          ? "Single Choice"
          : value === "MULTI"
            ? "Multiple Choice"
            : value === "TF"
              ? "True / False"
              : "Description",
    },
    {
      Header: "Marks",
      accessor: "marks",
      Cell: ({ value }) => (
        <span className="font-weight-bold">
          {value !== "" && value !== undefined ? value : "-"}
        </span>
      ),
    },
    {
      Header: "Actions",
      Cell: ({ row }) => {
        const id = row.index;

        return (
          <div className="d-flex align-items-center">
            {/* View */}
            <Button
              color="link"
              className="p-0 mr-2"
              id={`viewQuestion${id}`}
              onClick={() => openViewQuestion(row.original, row.index)}
            >
              <i className="fa fa-edit mr-1" />
            </Button>
            <UncontrolledTooltip target={`viewQuestion${id}`}>
              Edit
            </UncontrolledTooltip>

            {/* Remove */}
            <Button
              color="link"
              className="btn-link d-flex align-items-center p-0 table-action-delete"
              id={`deleteQuestion${id}`}
              onClick={() =>
                setQbQuestions((prev) => prev.filter((_, i) => i !== row.index))
              }
            >
              <i className="fas fa-trash mx-1 table-action-delete" />
            </Button>
            <UncontrolledTooltip target={`deleteQuestion${id}`}>
              Delete
            </UncontrolledTooltip>
          </div>
        );
      },
    },
  ];

  const deriveStatus = (from, till) => {
    const now = new Date();
    if (now < new Date(from)) return "UPCOMING"; // before start
    if (now > new Date(till)) return "CLOSED"; // after end
    return "ACTIVE"; // between start & end
  };

  return (
    <>
      {viewMode === "LIST" && (
        <>
          <Row>
            <Col md="12">
              <Card>
                <CardHeader className="d-flex align-items-center justify-content-between">
                  <div className="d-flex flex-wrap align-items-center">
                    <Input
                      type="text"
                      placeholder="Search by assessment, course, batch"
                      className="mr-2 mb-2 filter-input-large"
                      value={searchState.searchString}
                      onChange={(e) =>
                        setSearchState((prev) => ({
                          ...prev,
                          searchString: e.target.value,
                        }))
                      }
                      onKeyDown={handleKeyDown}
                    />

                    <Select
                      className="mr-2 mb-2"
                      options={ASSESSMENT_STATUS_OPTIONS}
                      placeholder="Select Status"
                      styles={fixedWidthCustomStyle()}
                      components={{ Option, SingleValue }}
                    />

                    <Button
                      className="mb-2 btn-deep-blue"
                      onClick={handleSearch}
                    >
                      <i className="fa fa-search mr-1" /> Search
                    </Button>
                  </div>

                  <Button className="btn-deep-blue" onClick={openAddModal}>
                    <i className="fa fa-plus mr-1" />
                    Assign Assessment
                  </Button>
                </CardHeader>

                <CardBody>
                  <CommonTable
                    data={assessmentList}
                    columns={columns}
                    pageNum={pageNum}
                    setPageNum={setPageNum}
                    defaultPageSize={DEFAULT_RECORDS_PER_PAGE}
                    showPaginationBottom
                    totalPages={Math.ceil(
                      assessmentList.length / DEFAULT_RECORDS_PER_PAGE,
                    )}
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </>
      )}

      {viewMode !== "LIST" && (
        <>
          <Card>
            <CardHeader className="d-flex align-items-center">
              <i
                className="fa-solid fa-angle-left cursor mr-2"
                onClick={goBackToList}
              ></i>

              <h4 className="mb-0">
                {viewMode === "ADD" ? "Assign Assessment" : "Edit Assessment"}
              </h4>
            </CardHeader>

            <Form onSubmit={(e) => e.preventDefault()}>
              <CardBody>
                <Row>
                  <Col md="4" className="mb-3">
                    <FormGroup>
                      <Label className="form-control-label">
                        Assessment Name <span className="text-danger">*</span>
                      </Label>
                      <Input
                        type="text"
                        placeholder="Enter assessment name"
                        value={formData.assessmentName.value}
                        onChange={(e) =>
                          handleInputChange("assessmentName", e.target.value)
                        }
                        onBlur={(e) =>
                          handleOnBlur("assessmentName", e.target.value)
                        }
                        className={
                          formData.assessmentName.error ? "is-invalid" : ""
                        }
                      />

                      {formData.assessmentName.error && (
                        <small className="text-danger">
                          {formData.assessmentName.error}
                        </small>
                      )}
                    </FormGroup>
                  </Col>

                  <Col md="4" className="mb-3">
                    <FormGroup>
                      <Label className="form-control-label d-block">
                        Assessment Duration (minutes)
                        <span className="text-danger">*</span>
                      </Label>

                      <Input
                        type="number"
                        min="1"
                        placeholder="Enter duration in minutes"
                        value={formData.assessmentDuration.value}
                        onChange={(e) =>
                          handleInputChange(
                            "assessmentDuration",
                            e.target.value,
                          )
                        }
                        onBlur={(e) =>
                          handleOnBlur("assessmentDuration", e.target.value)
                        }
                        className={
                          formData.assessmentDuration.error ? "is-invalid" : ""
                        }
                      />

                      {formData.assessmentDuration.error && (
                        <small className="text-danger">
                          {formData.assessmentDuration.error}
                        </small>
                      )}
                    </FormGroup>
                  </Col>

                  <Col md="4" className="mb-3">
                    <FormGroup>
                      <Label className="form-control-label d-block">
                        Passing Percentage{" "}
                        <span className="text-danger">*</span>
                      </Label>
                      <Input
                        type="text"
                        min="0"
                        max="100"
                        placeholder="Enter passing percentage"
                        value={formData.passingPercentage.value}
                        onChange={(e) =>
                          handleInputChange("passingPercentage", e.target.value)
                        }
                        onBlur={(e) =>
                          handleOnBlur("passingPercentage", e.target.value)
                        }
                        className={
                          formData.passingPercentage.error ? "is-invalid" : ""
                        }
                      />
                      {formData.passingPercentage.error && (
                        <small className="text-danger">
                          {formData.passingPercentage.error}
                        </small>
                      )}
                    </FormGroup>
                  </Col>

                  <Col md="4" className="mb-3">
                    <FormGroup>
                      <Label className="form-control-label d-block">
                        Available From <span className="text-danger">*</span>
                      </Label>
                      <DatePicker
                        selected={
                          formData.availableFrom.value
                            ? new Date(formData.availableFrom.value)
                            : null
                        }
                        onChange={(date) => handleInputChange("availableFrom", date)}
                        onBlur={() => handleOnBlur("availableFrom", formData.availableFrom.value)}
                        dateFormat="yyyy-MM-dd, hh:mm aa"
                        placeholderText="YYYY-MM-DD, hh:mm aa"
                        showTimeSelect
                        timeFormat="hh:mm aa"
                        timeIntervals={1}
                        minDate={new Date()}
                        showIcon
                        icon={
                          <i className="fa-solid fa-calendar-day fa-md mt-1 mr-4 text-muted" />
                        }
                        toggleCalendarOnIconClick
                        className={`form-control ${formData.availableFrom.error ? "is-invalid" : ""
                          }`}
                        wrapperClassName="w-100"
                        calendarClassName="custom-calendar"
                        popperPlacement="bottom-start"
                        onKeyDown={(e) => e.preventDefault()}
                        onPaste={(e) => e.preventDefault()}
                      />
                      {formData.availableFrom.error && (
                        <small className="text-danger">
                          {formData.availableFrom.error}
                        </small>
                      )}
                    </FormGroup>
                  </Col>

                  <Col md="4" className="mb-3">
                    <FormGroup>
                      <Label className="form-control-label d-block">
                        Available Till <span className="text-danger">*</span>
                      </Label>
                      <DatePicker
                        selected={
                          formData.availableTill.value
                            ? new Date(formData.availableTill.value)
                            : null
                        }
                        onChange={(date) => handleInputChange("availableTill", date)}
                        onBlur={() => handleOnBlur("availableTill", formData.availableTill.value)}
                        dateFormat="yyyy-MM-dd, hh:mm aa"
                        placeholderText="YYYY-MM-DD, hh:mm aa"
                        showTimeSelect
                        timeFormat="hh:mm aa"
                        timeIntervals={1}
                        minDate={formData.availableFrom.value}
                        showIcon
                        icon={
                          <i className="fa-solid fa-calendar-day fa-md mt-1 mr-4 text-muted" />
                        }
                        toggleCalendarOnIconClick
                        className={`form-control ${formData.availableTill.error ? "is-invalid" : ""
                          }`}
                        wrapperClassName="w-100"
                        calendarClassName="custom-calendar"
                        popperPlacement="bottom-start"
                        onKeyDown={(e) => e.preventDefault()}
                        onPaste={(e) => e.preventDefault()}
                      />
                      {formData.availableTill.error && (
                        <small className="text-danger">
                          {formData.availableTill.error}
                        </small>
                      )}
                    </FormGroup>
                  </Col>

                  <Col md="4" className="mb-3">
                    <FormGroup>
                      <Label className="form-control-label d-block">
                        Maximum Attempts <span className="text-danger">*</span>
                      </Label>
                      <Input
                        type="text"
                        min="1"
                        placeholder="Enter max attempts"
                        value={formData.maxAttempts.value}
                        onChange={(e) =>
                          handleInputChange("maxAttempts", e.target.value)
                        }
                        onBlur={(e) =>
                          handleOnBlur("maxAttempts", e.target.value)
                        }
                        className={
                          formData.maxAttempts.error ? "is-invalid" : ""
                        }
                      />
                      {formData.maxAttempts.error && (
                        <small className="text-danger">
                          {formData.maxAttempts.error}
                        </small>
                      )}
                    </FormGroup>
                  </Col>
                </Row>
                <div className="d-flex justify-content-between align-items-center pb-3">
                  <FormGroup className="mb-0">
                    <Label className="form-control-label">
                      Negative Grading
                      <InfoPopover
                        targetId="negativeGradingInfo"
                        title="Negative Grading"
                        content="When enabled, incorrect answers will reduce the total score based on the selected grade percentage."
                      />
                    </Label>

                    <div className="d-flex mt-2">
                      <FormGroup check className="mr-5">
                        <Label check className="cursor">
                          <Input
                            type="radio"
                            name="negativeGrading"
                            checked={negativeGrading === true}
                            onChange={() => setNegativeGrading(true)}
                          />
                          Yes
                        </Label>
                      </FormGroup>

                      <FormGroup check>
                        <Label check className="cursor">
                          <Input
                            type="radio"
                            name="negativeGrading"
                            checked={negativeGrading === false}
                            onChange={() => setNegativeGrading(false)}
                          />
                          No
                        </Label>
                      </FormGroup>
                    </div>
                  </FormGroup>
                  <div className="d-flex align-items-center justify-content-end">
                    <Button
                      color="default"
                      className="btn-deep-blue"
                      onClick={resetQBPanel}
                    >
                      <i className="fa fa-plus mr-1" />
                      Question
                    </Button>

                    <Button
                      className="btn-deep-blue mr-5"
                      onClick={() => setShowExistingModal(true)}
                    >
                      <i className="fa fa-plus mr-1 cursor" />
                      Existing Questions
                    </Button>
                  </div>
                </div>

                <CommonTable
                  columns={questionColumns}
                  data={qbQuestions.map((q, index) => ({
                    ...q,
                    srNo: index + 1,
                  }))}
                  showPaginationBottom={false}
                />

                <AddQuestion
                  negativeGrading={negativeGrading}
                  isOpen={isQBPanelOpen}
                  onClose={closeModal}
                  onSave={(question, index, mode) => {
                    if (mode === "VIEW") {
                      setQbQuestions((prev) =>
                        prev.map((q, i) => (i === index ? question : q)),
                      );
                    } else {
                      setQbQuestions((prev) => [...prev, question]);
                    }
                  }}
                  mode={qbPanelMode}
                  selectedIndex={selectedQuestionIndex}
                  initialData={currentQB}
                />
              </CardBody>

              <CardFooter className="text-right">
                <Button color="secondary" onClick={goBackToList}>
                  Cancel
                </Button>
                <Button color="deep-blue" onClick={handleSubmit}>
                  Save
                </Button>
              </CardFooter>
            </Form>
          </Card>
        </>
      )}
      <ExistingQuestionModal
        isOpen={showExistingModal}
        toggleModal={() => setShowExistingModal(false)}
        onSave={() => {
          setShowExistingModal(false);
          navigate(
            role === "SUPER_ADMIN"
              ? `/super-admin/question-bank/add/questions`
              : role === "CHANNEL_PARTNER"
                ? `/channel-partner/question-bank/add/questions`
                : `/admin/question-bank/add/questions`,
          );
        }}
      />
    </>
  );
}

export default Assessment;
